import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICES } from "@/data/services";
import { Img } from "@/components/Img";
import { EASE } from "@/components/Motion";
import { SectionLabel } from "@/components/SectionLabel";
import { CtaLink } from "@/components/CtaLink";

// Bento: 4 columns. Row 1-2: 2x2 feature + four 1x1. Row 3: 2x1 feature + two 1x1.
const TILES = [
  { span: "md:col-span-2 md:row-span-2", tone: "image" },
  { span: "", tone: "mist" },
  { span: "", tone: "navy" },
  { span: "", tone: "paper" },
  { span: "", tone: "lime" },
  { span: "md:col-span-2", tone: "image" },
  { span: "", tone: "navy" },
  { span: "", tone: "mist" },
];

const TONE = {
  mist: "bg-mist text-navy",
  navy: "on-dark bg-navy text-white",
  paper: "bg-white text-navy ring-1 ring-[var(--line)]",
  lime: "bg-lime text-navy",
  image: "on-dark bg-midnight text-white",
};

const Tile = ({ s, tile, i }) => (
  <motion.li
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 0.8, ease: EASE, delay: (i % 4) * 0.07 }}
    className={`${tile.span} min-h-[240px]`}
  >
    <Link
      to={`/services#${s.slug}`}
      data-testid={`home-service-tile-${s.slug}`}
      className={`group relative flex h-full flex-col justify-between overflow-hidden rounded-lg p-6 sm:p-7 ${TONE[tile.tone]}`}
    >
      {tile.tone === "image" && (
        <>
          <Img name={s.image} alt={s.alt} sizes="(min-width:768px) 50vw, 100vw" className="absolute inset-0 h-full w-full object-cover opacity-80 transition-transform duration-[1400ms] ease-expo group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-midnight via-midnight/40 to-transparent" aria-hidden="true" />
        </>
      )}
      <div className="relative flex items-start justify-between">
        <span className="font-mono text-xs opacity-80">{s.n}</span>
        <span className="grid h-10 w-10 place-items-center rounded-full border border-current/30 transition-[background-color,color,transform] duration-500 ease-expo group-hover:rotate-45 group-hover:bg-lime group-hover:text-navy">
          <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
        </span>
      </div>
      <div className="relative mt-10">
        <h3 className={`font-display font-bold tracking-tight ${tile.span.includes("row-span-2") ? "text-3xl sm:text-5xl" : "text-2xl"}`}>{s.title}</h3>
        <p className="mt-2 max-w-sm text-sm leading-relaxed opacity-85">{s.short}</p>
      </div>
    </Link>
  </motion.li>
);

export const ServicesBento = () => (
  <section className="pb-24 sm:pb-36" aria-labelledby="services-heading" data-testid="home-services">
    <div className="shell">
      <div className="mb-14 flex flex-col justify-between gap-8 md:flex-row md:items-end">
        <div>
          <SectionLabel index="02">Services</SectionLabel>
          <h2 id="services-heading" className="mt-6 max-w-2xl font-display text-4xl font-extrabold tracking-[-0.04em] text-navy sm:text-5xl lg:text-6xl">
            Eight disciplines. <span className="text-royal">One visual voice.</span>
          </h2>
        </div>
        <CtaLink to="/services" variant="ghost" testid="home-all-services-button">All services</CtaLink>
      </div>
      <ul className="grid auto-rows-[minmax(240px,auto)] gap-3 sm:grid-cols-2 md:grid-cols-4 md:auto-rows-[260px]">
        {SERVICES.map((s, i) => <Tile key={s.slug} s={s} tile={TILES[i]} i={i} />)}
      </ul>
    </div>
  </section>
);
